import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase/client";
import { Product } from "./types";

export interface MetaTags {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogUrl: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string;
  keywords?: string;
}

/**
 * Generate a URL-friendly slug from a product name
 * @param productName - The product name to convert
 * @returns URL-safe slug (e.g., "Kiss Cut Stickers" → "kiss-cut-stickers")
 */
export function generateProductSlug(productName: string): string {
  return productName
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-"); // Remove duplicate hyphens
}

/**
 * Fetch a product from Firestore by its slug
 * @param slug - The URL slug to look up
 * @returns Product if found, null otherwise
 */
export async function getProductFromSlug(slug: string): Promise<Product | null> {
  try {
    // First try to find by slug field
    const productsRef = collection(db, "products");
    const q = query(productsRef, where("slug", "==", slug));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return { ...doc.data(), id: doc.id } as Product;
    }

    // Fallback: find by generating slug from name (for backward compatibility)
    const allProductsQuery = query(productsRef, where("active", "!=", false));
    const allProductsSnapshot = await getDocs(allProductsQuery);

    for (const doc of allProductsSnapshot.docs) {
      const product = doc.data() as Product;
      const generatedSlug = generateProductSlug(product.name);
      if (generatedSlug === slug) {
        return { ...product, id: doc.id };
      }
    }

    return null;
  } catch (error) {
    console.error("Error fetching product by slug:", error);
    return null;
  }
}

/**
 * Generate meta tags for a product page
 * @param product - The product to generate meta tags for
 * @returns Complete meta tag object
 */
export function generateProductMetaTags(product: Product): MetaTags {
  const slug = product.slug || generateProductSlug(product.name);
  const baseUrl = "https://stickersby.ai";

  // Use custom meta title/description if available, otherwise use product name/description
  const title =
    product.metaTitle || `${product.name} | Custom AI-Designed Products | StickersBy AI`;
  const description =
    product.metaDescription ||
    `Create custom ${product.name.toLowerCase()} with AI. ${product.description}. Design in minutes with our AI-powered tool.`;

  // Use custom OG image if available, otherwise use product thumbnail
  const ogImage = product.ogImage || product.thumbnail || `${baseUrl}/og-image.jpg`;

  return {
    title,
    description,
    ogTitle: title,
    ogDescription: description,
    ogImage,
    ogUrl: `${baseUrl}/products/${slug}`,
    twitterTitle: title,
    twitterDescription: description,
    twitterImage: ogImage,
    keywords: `${product.name}, custom ${product.name.toLowerCase()}, AI products, AI design, ${product.categories?.join(", ") || "custom products"}`,
  };
}

/**
 * Get default meta tags for non-product pages
 * @returns Default meta tag object
 */
export function getDefaultMetaTags(): MetaTags {
  const baseUrl = "https://stickersby.ai";

  return {
    title: "StickersBy AI | Create Custom Products with AI",
    description:
      "Transform your ideas into custom products using AI. Create unique stickers, coffee mugs, t-shirts, candles, and more in minutes with our AI-powered design tool.",
    ogTitle: "StickersBy AI | Create Custom Products with AI",
    ogDescription:
      "Transform your ideas into custom products using AI. Create unique stickers, coffee mugs, t-shirts, and more.",
    ogImage: `${baseUrl}/og-image.jpg`,
    ogUrl: baseUrl,
    twitterTitle: "StickersBy AI | Create Custom Products with AI",
    twitterDescription: "Transform your ideas into custom products using AI.",
    twitterImage: `${baseUrl}/og-image.jpg`,
    keywords:
      "AI stickers, custom stickers, AI products, custom mugs, custom t-shirts, AI-generated products, custom candles, AI design tool",
  };
}
